import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const colorMap: Record<string, string> = {
  "custom.norisk": "#A4CF87",
  "custom.blue4": "#8697C4",
  "custom.blue6": "#5A6AA5",
  "custom.blue3": "#ADBBDA",
  "custom.riskverylow": "#FFDA65",
  "custom.risklow": "#FEDEDE",
  "custom.riskmoderate": "#FDB5B5",
  "custom.riskhigh": "#FB8585",
  "custom.riskveryhigh": "#F84242",
  "custom.riskcritical": "#E20808",
  "custom.public": "#D0D8FD",
};

type DonutChartData = {
  name: string;
  noOfItems: number;
  color: string;
  value: string;
};

type CRQCDashboardProps = {
  absoluteYearsData: DonutChartData[];
  title: string;
  footer: string;
};

const DonutChart: React.FC<CRQCDashboardProps> = ({
  absoluteYearsData,
  title,
  footer,
}) => {
  const renderDonutChart = (data: DonutChartData[]) => {
    const total = data.reduce((sum, entry) => sum + entry.noOfItems, 0);
    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        return (
          <div className="bg-white shadow-md p-2 border border-gray-300 rounded">
            <p className="">
              {payload[0].name}:&nbsp;{payload[0].value}
            </p>
          </div>
        );
      }
      return null;
    };

    return (
      <div
        className="flex flex-col items-start w-full p-1"
        style={{ height: "330px" }}
      >
        {/* Title */}
        <h3 className="text-m font-bold text-custom-blue7 mb-2">{title}</h3>
        <div className="w-full h-0.5 bg-gray-300 mb-4"></div>
        {/* Chart and Legend Row */}
        <div className="flex flex-row items-center justify-around w-full">
          {/* Donut Chart */}
          <div className="relative w-60 h-60">
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="noOfItems"
                  nameKey="name"
                  innerRadius="60%"
                  outerRadius="90%"
                  paddingAngle={0}
                  stroke="none"
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={colorMap[entry.color] || "#000"}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-20 flex items-center justify-center">
              <div className="text-xl font-bold text-custom-blue7">{total}</div>
            </div>
          </div>

          {/* Legend */}
          <div className="ml-8">
            {data.map((entry, index) => (
              <div
                key={index}
                className="flex items-center justify-between mb-2"
              >
                <div className="flex items-center">
                  {/* Color Indicator */}
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: colorMap[entry.color] || "#000" }}
                  ></div>
                  {/* Label */}
                  <span className="ml-2 text-custom-greytext text-sm">
                    {entry.name}
                  </span>
                </div>
                {/* Value */}
                <div className="text-custom-blue6 font-medium ml-4 text-sm">
                  {entry.value}
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Footer */}
        <p className="text-custom-blue6">{footer}</p>
      </div>
    );
  };

  return (
    <div className="mx-auto p-6 bg-white rounded-md shadow-md">
      {/* Donut Charts */}
      <div className="flex flex-wrap justify-center ">
        {renderDonutChart(absoluteYearsData)}
      </div>
    </div>
  );
};

export default DonutChart;
